import React, {useContext, useEffect, useState} from "react";

import {log} from "../../ApiCalls";

import "../../../../style/PriorityTableStyles/actionButton.css";

import {ActionButtonModeEnum} from "../../../../Types/ActionButtonMode"
import {StatusEnum} from "../../../../Types/StatusEnum";
import {RolesEnum} from "../../../../Types/RolesEnum";
import {isBookSource} from "../../../../Types/Sources";
import SetToReviewModal from "./SetToReviewModal";
import RevisionModal from "./RevisionModal";
import ManuscriptModal from "./ManuscriptModal";
import ContentEditorApproveModal from "./ContentEditorApproveModal";
import {TaskType} from "../../../../Types/Task";
import {azureTokenContext, userIdContext} from "../../../../App";
import {makeMetricPayloadByPriorityNumber} from "../OpenDocumentDropdown";
import {useLogMetric} from "../../../../hooks/useLogMetric";

export default function ActionButton({task, allTasks, setAllTasks, setSelectedTasks, useNewManuscriptFlow = true}: {
    task: TaskType,
    allTasks: TaskType[],
    setAllTasks: React.Dispatch<React.SetStateAction<TaskType[]>>,
    setSelectedTasks: React.Dispatch<React.SetStateAction<TaskType[]>>,
    useNewManuscriptFlow?: boolean
}) {
    const userId = useContext(userIdContext);
    const azureToken = useContext(azureTokenContext);
    const logMetric = useLogMetric();

    const [buttonMode, setButtonMode] = useState<ActionButtonModeEnum>(ActionButtonModeEnum.none);
    const [isSetToReviewModalOpen, setIsSetToReviewModalOpen] = useState(false);
    const [isContentEditorApproveModalOpen, setIsContentEditorApproveModalOpen] = useState(false);
    const [isRevisionModalOpen, setIsRevisionModalOpen] = useState(false);
    const [isManuscriptModalOpen, setIsManuscriptModalOpen] = useState(false);

    useEffect(() => {
        // A book starts only from Backoffice (P2-D3): no author-created work item (revision action) for a book.
        if (!task.WorkItemId && !task.AuthorCreatedWI) {
            setButtonMode(isBookSource(task.Source) ? ActionButtonModeEnum.none : ActionButtonModeEnum.update)
        } else if (task.Status === StatusEnum.init && task.Role === RolesEnum.author) {
            setButtonMode(ActionButtonModeEnum.setToReview)
        } else if (task.Status === StatusEnum.init && task.Role === RolesEnum.contentEditor && task.AuthorEmail) {
            setButtonMode(ActionButtonModeEnum.emailToAuthor)
        } else if (task.Status === StatusEnum.readyForReview && task.Role === RolesEnum.contentEditor  && !task.GotApprovalFromContentReviewer && !task.IsReviewed) {
            setButtonMode(ActionButtonModeEnum.contentEditorApprove)
        } else {
            setButtonMode(ActionButtonModeEnum.none)
        }

    }, [task.Role, task.Status, task.WorkItemId, task.AuthorEmail, task.GotApprovalFromContentReviewer, task.IsReviewed, task.AuthorCreatedWI, task.Source]);

    const mailtoAuthorLink = () => {
        const authorEmail = task.AuthorEmail;
        const subject = encodeURIComponent(`Karnov - ${task.TaskName}`);
        const body = encodeURIComponent(`Hei, ${task.Author || "forfatter"}!\n\nDet er flott om du logger deg inn i Karnovs forfatterportal og følger opp oppgaven(e) knyttet til ditt manus til "${task.TaskName}". Ta gjerne kontakt med meg hvis du trenger hjelp. Du kan også kontakte redaksjonen (redaksjonen@karnovgroup.no) eller support (support@karnovgroup.no).\n\nVennlig hilsen\nFagredaktør, Karnov`);
        return `mailto:${authorEmail}?subject=${subject}&body=${body}`;
    }

    const logClickOnEmailToAuthorButton = () => {
        log("INFO", `ContentEditor clicked on email to author button for WorkItemId: ${task.WorkItemId}`, "AuthorsPortal", task.WorkItemId, "ContentEditor", userId, azureToken);
        const payload = makeMetricPayloadByPriorityNumber(task.PriorityNumber);
        logMetric({category: "ActionButton", subCategory: "Email to author clicked", message: "ContentEditor clicked on email to author button", workItemId: task.WorkItemId, userRole: "ContentEditor", payload});
    }

    return (
        <div className="action-button-container">
            {buttonMode === ActionButtonModeEnum.none && <></>}
            {buttonMode === ActionButtonModeEnum.update &&
                <>
                    {task.Role === RolesEnum.author && (
                        <button
                            className="action-button"
                            onClick={() => {
                                if (useNewManuscriptFlow) {
                                    setIsManuscriptModalOpen(true);
                                    logMetric({category: "ActionButton", subCategory: "Manuscript modal opened", message: `User opened manuscript modal for WorkItemId: ${task.WorkItemId}`, workItemId: task.WorkItemId, userRole: task.Role, payload: makeMetricPayloadByPriorityNumber(task.PriorityNumber)});
                                } else {
                                    setIsRevisionModalOpen(true);
                                    logMetric({category: "ActionButton", subCategory: "Revision modal opened", message: `User opened revision modal for WorkItemId: ${task.WorkItemId}`, workItemId: task.WorkItemId, userRole: task.Role, payload: makeMetricPayloadByPriorityNumber(task.PriorityNumber)});
                                }
                            }}
                        >
                            Oppdater
                        </button>
                    )}
                </>
            }
            {buttonMode === ActionButtonModeEnum.setToReview &&
                <button className="action-button" onClick={() => {
                    setIsSetToReviewModalOpen(true);
                    logMetric({category: "ActionButton", subCategory: "Set to review modal opened", message: `User opened set to review modal for WorkItemId: ${task.WorkItemId}`, workItemId: task.WorkItemId, userRole: "Author", payload: makeMetricPayloadByPriorityNumber(task.PriorityNumber)});
                }}>Sett til
                    vurdering</button>
            }
            {buttonMode === ActionButtonModeEnum.contentEditorApprove &&
                <button className="action-button" onClick={() => {
                    setIsContentEditorApproveModalOpen(true);
                    logMetric({category: "ActionButton", subCategory: "ContentEditor approve modal opened", message: `ContentEditor opened approve modal for WorkItemId: ${task.WorkItemId}`, workItemId: task.WorkItemId, userRole: "ContentEditor", payload: makeMetricPayloadByPriorityNumber(task.PriorityNumber)});
                }}>
                    Manus vurdert
                </button>
            }
            {buttonMode === ActionButtonModeEnum.emailToAuthor &&
                <a href={mailtoAuthorLink()} onClick={logClickOnEmailToAuthorButton}>
                    <button className="action-button">Purr på forfatter</button>
                </a>
            }

            <SetToReviewModal isOpen={isSetToReviewModalOpen}
                              setIsOpen={setIsSetToReviewModalOpen}
                              task={task}
                              setAllTasks={setAllTasks}
                              setSelectedTasks={setSelectedTasks}
            />
            <ContentEditorApproveModal isOpen={isContentEditorApproveModalOpen}
                                       setIsOpen={setIsContentEditorApproveModalOpen}
                                       task={task}
                                       setAllTasks={setAllTasks}
                                       setSelectedTasks={setSelectedTasks}
            />

            <RevisionModal
                isOpen={isRevisionModalOpen}
                setIsOpen={setIsRevisionModalOpen}
                task={task}
                setAllTasks={setAllTasks}
                setSelectedTasks={setSelectedTasks}
            />

            <ManuscriptModal
                isOpen={isManuscriptModalOpen}
                setIsOpen={setIsManuscriptModalOpen}
                triggerTask={task}
                allTasks={allTasks}
                setAllTasks={setAllTasks}
                setSelectedTasks={setSelectedTasks}
                useNewFlow={useNewManuscriptFlow}
            />
        </div>
    );

}
